<template>
  <div v-if="visible" class="TicketHistory Card Card--top">
    <div class="Card-header">
      <button
        type="button"
        class="Card-headerControl Button TicketHistory-close"
        data-cy="ticket-history-close"
        aria-label="Close ticket history"
        @click="$emit('close')">
        &times;
      </button>
      <h2 class="Card-title">Ticket history</h2>
      <p class="TicketHistory-subtitle">
        {{ ticketCode }}<span v-if="meta.total != null"> · {{ meta.total }} {{ entryLabel }}</span>
      </p>
    </div>

    <div class="Card-body">
      <p v-if="errorMessage && !displayEntries.length" class="Error" data-cy="ticket-history-error">{{ errorMessage }}</p>

      <p
        v-else-if="loading && !displayEntries.length"
        class="TicketHistory-loading"
        data-cy="ticket-history-loading">
        Loading activity…
      </p>

      <p
        v-else-if="!displayEntries.length"
        class="TicketHistory-empty"
        data-cy="ticket-history-empty">
        No activity recorded for this ticket yet. Operations before June 2026 may not appear.
      </p>

      <ol v-else class="TicketHistory-timeline" role="list">
        <li
          v-for="entry in displayEntries"
          :key="entry.id"
          class="TicketHistory-row"
          data-cy="ticket-history-row"
          tabindex="0">
          <div class="TicketHistory-marker" :class="iconClass(entry.event_type)" aria-hidden="true" />

          <div class="TicketHistory-content">
            <p class="TicketHistory-timestamp">{{ formatTimestamp(entry.created_at) }}</p>

            <p class="TicketHistory-headline">
              <span class="TicketHistory-eventType">{{ eventLabel(entry.event_type) }}</span>
              <span
                v-if="!entry.synthetic && entry.outcome"
                class="Chip TicketHistory-outcome"
                :class="outcomeClass(entry.outcome)">
                {{ outcomeLabel(entry.outcome) }}
              </span>
            </p>

            <p v-if="!entry.synthetic && summaryFor(entry)" class="TicketHistory-summary">{{ summaryFor(entry) }}</p>

            <p v-if="!entry.synthetic" class="TicketHistory-meta">
              <span>Actor: User #{{ entry.actor_user_id }}</span>
              <span v-if="entry.operation_id" class="TicketHistory-operation">
                · op {{ truncateOp(entry.operation_id) }}
                <button
                  type="button"
                  class="Button Button--secondary TicketHistory-copy"
                  :data-cy="'ticket-history-copy-' + entry.id"
                  @click="copyOperationId(entry.operation_id)">
                  {{ copiedOperationId === entry.operation_id ? 'Copied' : 'Copy' }}
                </button>
              </span>
            </p>

            <p v-if="!entry.synthetic && relatedCodes(entry).length" class="TicketHistory-related">
              Related:
              <router-link
                v-for="code in relatedCodes(entry)"
                :key="code"
                class="TicketHistory-relatedLink"
                :to="ticketSearchRoute(code)">
                {{ code }}
              </router-link>
            </p>

            <button
              v-if="!entry.synthetic"
              type="button"
              class="Button Button--secondary TicketHistory-detailsToggle"
              :aria-expanded="isExpanded(entry.id) ? 'true' : 'false'"
              @click="toggleExpanded(entry.id)"
              @keydown.enter.prevent="toggleExpanded(entry.id)">
              {{ isExpanded(entry.id) ? 'Hide details ▴' : 'View details ▾' }}
            </button>

            <pre
              v-if="!entry.synthetic && isExpanded(entry.id)"
              class="TicketHistory-rawPayload">{{ formattedPayload(entry.payload) }}</pre>
          </div>
        </li>
      </ol>

      <p
        v-if="loading && displayEntries.length && !entries.length"
        class="TicketHistory-loading"
        data-cy="ticket-history-loading">
        Loading activity…
      </p>

      <div v-if="canLoadMore" class="TicketHistory-loadMoreWrap">
        <button
          type="button"
          class="Button TicketHistory-loadMore"
          data-cy="ticket-history-load-more"
          :disabled="loadingMore"
          @click="loadMore">
          {{ loadingMore ? 'Loading…' : 'Load more' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import cookieModel from '../models/cookie'
import errorModel from '../models/error'
import {
  formatActivityTimestamp,
  formatPayloadSummary,
  getEventTypeIconClass,
  getEventTypeLabel,
  getOutcomeChipClass,
  getOutcomeLabel,
  getRelatedTicketCodes,
  sanitizePayloadForDisplay,
  truncateOperationId,
} from '../models/ticket-activity'
import { buildTicketSearchRoute } from '../pages/Search/search-utils'
import './_TicketHistory.scss'

export default {
  props: {
    ticketCode: {
      type: String,
      required: true,
    },
    purchasedAt: {
      type: String,
      default: '',
    },
    visible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      baseUrl: process.env.VUE_APP_API_URL,
      axiosInstance: axios.create(),
      entries: [],
      meta: {},
      loading: false,
      loadingMore: false,
      errorMessage: '',
      expandedIds: {},
      copiedOperationId: null,
    }
  },
  computed: {
    canLoadMore() {
      const current = Number(this.meta.current_page) || 0
      const last = Number(this.meta.last_page) || 0
      return current > 0 && last > current
    },
    entryLabel() {
      const total = Number(this.meta.total) || 0
      return total === 1 ? 'entry' : 'entries'
    },
    displayEntries() {
      const rows = []
      if (this.purchasedAt) {
        rows.push({
          id: '__purchase__',
          synthetic: true,
          event_type: 'purchase',
          created_at: this.purchasedAt,
        })
      }
      return rows.concat(this.entries)
    },
  },
  watch: {
    ticketCode: {
      immediate: false,
      handler() {
        if (this.visible) {
          this.reload()
        }
      },
    },
    visible(isVisible) {
      if (isVisible) {
        this.reload()
      }
    },
  },
  mounted() {
    if (this.visible) {
      this.fetchActivity(1)
    }
  },
  methods: {
    requestUrl(path) {
      const normalisedPath = path.startsWith('/') ? path : `/${path}`
      const base = this.baseUrl
      if (!base || base === '/') {
        return normalisedPath
      }
      return String(base).replace(/\/+$/, '') + normalisedPath
    },
    applyAuthHeader() {
      if (!this.axiosInstance.defaults) {
        this.axiosInstance.defaults = { headers: { common: {} } }
      }
      if (!this.axiosInstance.defaults.headers) {
        this.axiosInstance.defaults.headers = { common: {} }
      }
      if (!this.axiosInstance.defaults.headers.common) {
        this.axiosInstance.defaults.headers.common = {}
      }
      this.axiosInstance.defaults.headers.common.Authorization =
        `Bearer ${cookieModel.getCookie('ticketlab_access')}`
    },
    resolveErrorMessage(error) {
      const status = error.response && error.response.status
      if (status === 403) {
        return 'Admin access required'
      }
      if (status === 404) {
        const reason = error.response.data && error.response.data.reason
        return reason || 'Ticket not found'
      }
      return errorModel.apiError(error, this.$router) || 'Failed to load ticket history'
    },
    fetchActivity(page, append = false) {
      if (!this.ticketCode) {
        return Promise.resolve()
      }

      if (append) {
        this.loadingMore = true
      } else {
        this.loading = true
        this.errorMessage = ''
      }

      this.applyAuthHeader()

      return this.axiosInstance
        .get(this.requestUrl('/einstein/ticket/activity'), {
          params: {
            ticket_code: this.ticketCode,
            page,
            per_page: 25,
          },
        })
        .then((response) => {
          const body = response.data || {}
          const rows = Array.isArray(body.data) ? body.data : []
          this.meta = body.meta || {}
          this.entries = append ? this.entries.concat(rows) : rows
          this.errorMessage = ''
          if (!append) {
            this.$emit('loaded', { total: this.meta.total || rows.length })
          }
        })
        .catch((error) => {
          if (!append) {
            this.entries = []
            this.errorMessage = this.resolveErrorMessage(error)
          }
        })
        .finally(() => {
          this.loading = false
          this.loadingMore = false
        })
    },
    reload() {
      this.expandedIds = {}
      return this.fetchActivity(1, false)
    },
    loadMore() {
      const nextPage = (Number(this.meta.current_page) || 1) + 1
      return this.fetchActivity(nextPage, true)
    },
    eventLabel(eventType) {
      return getEventTypeLabel(eventType)
    },
    iconClass(eventType) {
      return getEventTypeIconClass(eventType)
    },
    outcomeLabel(outcome) {
      return getOutcomeLabel(outcome)
    },
    outcomeClass(outcome) {
      return getOutcomeChipClass(outcome)
    },
    summaryFor(entry) {
      return formatPayloadSummary(entry.event_type, entry.payload)
    },
    formatTimestamp(createdAt) {
      return formatActivityTimestamp(createdAt)
    },
    truncateOp(operationId) {
      return truncateOperationId(operationId)
    },
    relatedCodes(entry) {
      return getRelatedTicketCodes(entry)
    },
    ticketSearchRoute(code) {
      return buildTicketSearchRoute(code)
    },
    isExpanded(entryId) {
      return Boolean(this.expandedIds[entryId])
    },
    toggleExpanded(entryId) {
      this.$set(this.expandedIds, entryId, !this.expandedIds[entryId])
    },
    formattedPayload(payload) {
      return JSON.stringify(sanitizePayloadForDisplay(payload), null, 2)
    },
    copyOperationId(operationId) {
      if (!operationId || !navigator.clipboard || !navigator.clipboard.writeText) {
        return
      }
      navigator.clipboard
        .writeText(String(operationId))
        .then(() => {
          this.copiedOperationId = operationId
          window.setTimeout(() => {
            if (this.copiedOperationId === operationId) {
              this.copiedOperationId = null
            }
          }, 2000)
        })
        .catch(() => {})
    },
  },
}
</script>
